// ProfileView (Task 13.6).
//
// Displays a Restaurant or NGO profile: Total Meals, Donations Completed, and
// Achievements Earned (each derived only from Completed donations, Req 31.1),
// the Badge Collection with an empty indication (Req 31.2), next meals-based
// badge progress (Req 30.8), a Call button (Req 18.4), and a Report User
// button (Req 17.1). Self-reporting is not offered for one's own profile
// (Req 17.4 is also enforced server-side).

import { useState } from "react";
import {
  REPORT_REASONS,
  ngoOverviewCounts,
  restaurantOverviewCounts,
  type Achievement,
  type Donation,
  type ReportReason,
} from "@sharebite/domain";
import { AsyncSection } from "../../components/AsyncSection";
import { CallButton } from "../../components/CallButton";
import { BadgeList, Card, Stat } from "../../components/primitives";
import { formatNextBadge, nextMealsBadge } from "../../lib/badgeDisplay";
import * as api from "../../lib/api";

interface ProfileData {
  totalMeals: number;
  donationsCompleted: number;
  achievements: Achievement[];
}

export function ProfileView({
  profileUid,
  profileName,
  role,
  viewerUid,
}: {
  profileUid: string;
  profileName: string;
  role: "Restaurant" | "NGO";
  viewerUid: string;
}): JSX.Element {
  const isOwnProfile = viewerUid === profileUid;

  async function load(): Promise<ProfileData> {
    const donations: Donation[] =
      role === "Restaurant"
        ? await api.listDonationsByRestaurant(profileUid)
        : await api.listDonationsByReserver(profileUid);
    const achievements = await api.listAchievements(profileUid);
    if (role === "Restaurant") {
      const c = restaurantOverviewCounts(donations);
      return {
        totalMeals: c.mealsDonated,
        donationsCompleted: c.mealsCompleted,
        achievements,
      };
    }
    const c = ngoOverviewCounts(donations);
    return {
      totalMeals: c.mealsReceived,
      donationsCompleted: c.completedClaims,
      achievements,
    };
  }

  return (
    <div className="sb-main">
      <div className="sb-row">
        <h1>{profileName}</h1>
        <span className="sb-pill">{role}</span>
        <span className="sb-spacer" />
        <CallButton
          targetUid={profileUid}
          label={role === "Restaurant" ? "Call Restaurant" : "Call NGO"}
        />
        {!isOwnProfile && (
          <ReportUserButton targetUid={profileUid} />
        )}
      </div>

      <AsyncSection title="Profile" loader={load} emptyText="">
        {(data: ProfileData) => {
          const next = nextMealsBadge(role, data.totalMeals);
          const earnedBadges = data.achievements.map((a) => a.badgeId);
          return (
            <div>
              <div className="sb-grid">
                <Stat label="Total Meals" value={data.totalMeals} />
                <Stat
                  label="Donations Completed"
                  value={data.donationsCompleted}
                />
                <Stat
                  label="Achievements Earned"
                  value={data.achievements.length}
                />
              </div>

              <h3>Badge Collection</h3>
              <BadgeList badges={earnedBadges} />

              {next && (
                <p className="sb-muted" data-testid="next-badge">
                  {formatNextBadge(role, next)}
                </p>
              )}
            </div>
          );
        }}
      </AsyncSection>
    </div>
  );
}

function ReportUserButton({ targetUid }: { targetUid: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | "">("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(): Promise<void> {
    if (reason === "") {
      // A reason is required (Req 17.3).
      setError("Please select a reason.");
      return;
    }
    setError(null);
    try {
      await api.fileReport({ targetUid, reason });
      setDone(true);
      setOpen(false);
    } catch {
      setError("Could not file the report.");
    }
  }

  if (done) {
    return <span className="sb-muted">Report submitted.</span>;
  }

  return (
    <span>
      <button
        type="button"
        className="sb-btn sb-btn--danger"
        onClick={() => setOpen((o) => !o)}
      >
        Report User
      </button>
      {open && (
        <Card title="Report user">
          <div className="sb-field">
            <label htmlFor="report-reason">Reason</label>
            <select
              id="report-reason"
              className="sb-select"
              value={reason}
              onChange={(e) => setReason(e.target.value as ReportReason)}
            >
              <option value="">Select a reason</option>
              {REPORT_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          {error && <p className="sb-field__error">{error}</p>}
          <button type="button" className="sb-btn" onClick={() => void submit()}>
            Submit report
          </button>
        </Card>
      )}
    </span>
  );
}
